import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate, getWeekNumber, formatCurrency, formatPercentage } from '../utils/helpers';
import { 
  Plus, Bell, CheckSquare, FileText, AlertTriangle, TrendingUp, TrendingDown, 
  Calendar, Users, Target, Eye, Trash2, Edit, ChevronRight, Clock,
  Zap, MessageSquare, ClipboardList, BarChart3, CheckCircle2, XCircle,
  Layers, Star, Flag, ArrowRight, ClipboardCheck, RefreshCw, ChevronDown, ChevronUp,
  Share2, Link, Copy, Lock, ExternalLink, Euro, Percent, ShoppingCart, Wallet,
  ArrowUpRight, ArrowDownRight, Minus, Building, Utensils, Lightbulb
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart
} from 'recharts';

// Bedrijfsoverzicht Component
const BedrijfsOverzicht = ({ 
  revenue, costs, goals, tasks, campaigns, events, arrangements, ideas, reviews, businessSettings,
  overviewShares, setOverviewShares, overviewShareDialog, setOverviewShareDialog,
  overviewShareForm, setOverviewShareForm, overviewShareLoading, setOverviewShareLoading, api
}) => {
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  // Calculate year revenue/costs totals
  const calcYearTotals = (data, field, year) => {
    return data.filter(d => {
      const date = new Date(d.date || d.period_start);
      return date.getFullYear() === year;
    }).reduce((sum, d) => sum + (d[field] || d.amount || 0), 0);
  };

  const calcYearCosts = (data, year) => {
    return data.filter(c => {
      const date = new Date(c.date || c.period_start);
      return date.getFullYear() === year;
    }).reduce((sum, c) => {
      return sum + (c.food_cost || 0) + (c.beverage_cost || 0) + (c.staff_cost || 0) + 
             (c.fixed_costs || 0) + (c.marketing_cost || 0) + (c.other_cost || 0);
    }, 0);
  };

  // Revenue & Costs
  const currentRevenue = calcYearTotals(revenue, 'amount', currentYear);
  const previousRevenue = calcYearTotals(revenue, 'amount', previousYear);
  // If previous year has no data but current year does, show 100% growth
  const revenueGrowth = previousRevenue > 0 
    ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 
    : (currentRevenue > 0 ? 100 : 0);

  const currentCosts = calcYearCosts(costs, currentYear);
  const previousCosts = calcYearCosts(costs, previousYear);
  // If previous year has no data but current year does, show 100% growth
  const costsChange = previousCosts > 0 
    ? ((currentCosts - previousCosts) / previousCosts) * 100 
    : (currentCosts > 0 ? 100 : 0);

  // Google Rating & Reviews
  const googleRating = businessSettings?.google_rating || 0;
  const currentYearReviews = reviews.filter(r => new Date(r.date || r.created_at).getFullYear() === currentYear).length;
  const previousYearReviews = reviews.filter(r => new Date(r.date || r.created_at).getFullYear() === previousYear).length;
  const reviewsGrowth = currentYearReviews - previousYearReviews;

  // Successful Campaigns (completed, afgerond, afgelopen, or with good performance)
  // Note: Dutch status values include "actief", "afgerond", "afgelopen", "gepauzeerd"
  const successfulCampaigns = campaigns.filter(c => 
    c.status === 'completed' || c.status === 'afgerond' || c.status === 'voltooid' ||
    c.status === 'afgelopen' ||
    c.performance === 'goed' || c.performance === 'excellent' || c.performance === 'uitstekend'
  );

  // Successful Events (past events with any status or guests attended)
  // Note: Dutch status values include "gepland", "bevestigd", "afgerond", "geannuleerd"
  const successfulEvents = events.filter(e => {
    const eventDate = new Date(e.date);
    const isPast = eventDate < new Date();
    return isPast || e.status === 'afgerond' || e.status === 'voltooid' || 
           e.status === 'bevestigd' || e.guests_attended > 0;
  });

  // Successful Arrangements (booked, confirmed, completed)
  // Note: Dutch status values include "beschikbaar", "geboekt", "bevestigd", "afgerond"
  const successfulArrangements = arrangements.filter(a => 
    a.status === 'completed' || a.status === 'afgerond' || a.status === 'bevestigd' ||
    a.status === 'geboekt' || a.status === 'voltooid'
  );

  // Achieved Goals
  const achievedGoals = goals.filter(g => g.status === 'completed' || g.status === 'behaald' || g.progress >= 100);

  // Worked out Ideas - include Dutch status values
  // Note: Dutch status values include "nieuw", "in_behandeling", "geimplementeerd", "afgewezen"
  const completedIdeas = ideas.filter(i => 
    i.status === 'completed' || i.status === 'afgerond' || i.status === 'converted' ||
    i.status === 'geimplementeerd' || i.status === 'voltooid' || i.status === 'uitgevoerd'
  );

  // Calculate profit growth (for display)
  const currentProfit = currentRevenue - currentCosts;
  const previousProfit = previousRevenue - previousCosts;
  const profitGrowth = previousProfit > 0 
    ? ((currentProfit - previousProfit) / previousProfit) * 100 
    : (currentProfit > 0 ? 100 : 0);
  
  // Calculate profit margin growth
  const currentMargin = currentRevenue > 0 ? (currentProfit / currentRevenue) * 100 : 0;
  const previousMargin = previousRevenue > 0 ? (previousProfit / previousRevenue) * 100 : 0;
  const marginChange = previousMargin > 0 
    ? currentMargin - previousMargin 
    : (currentMargin > 0 ? currentMargin : 0);

  // Share functions
  const handleCreateShare = async () => {
    if (!overviewShareForm.name || !overviewShareForm.password) {
      toast.error('Vul naam en wachtwoord in');
      return;
    }
    setOverviewShareLoading(true);
    try {
      const res = await api.post('/overview-shares', {
        name: overviewShareForm.name,
        password: overviewShareForm.password
      });
      toast.success('Deelbare link aangemaakt');
      setOverviewShareDialog(false);
      setOverviewShareForm({ name: '', password: '' });
      // Refresh shares
      const sharesRes = await api.get('/overview-shares');
      setOverviewShares(sharesRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij aanmaken link');
    } finally {
      setOverviewShareLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    if (!window.confirm('Weet je zeker dat je deze link wilt intrekken?')) return;
    try {
      await api.delete(`/overview-shares/${shareId}`);
      toast.success('Link ingetrokken');
      setOverviewShares(overviewShares.filter(s => s.id !== shareId && s.share_id !== shareId));
    } catch (error) {
      toast.error('Fout bij intrekken');
    }
  };

  const handleCopyLink = (shareId) => {
    const url = `${window.location.origin}/bedrijfsoverzicht/${shareId}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link gekopieerd!');
    }).catch(() => {
      toast.info(`Link: ${url}`);
    });
  };

  const handleExportPDF = () => {
    // Create printable content and trigger print dialog
    const printContent = document.getElementById('bedrijfsoverzicht-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Bedrijfsoverzicht ${currentYear}</title>
            <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #1C1917; }
              h1 { color: #1A2E05; font-size: 28px; margin-bottom: 10px; }
              h2 { color: #1A2E05; font-size: 20px; margin-top: 30px; margin-bottom: 15px; border-bottom: 2px solid #E7E5E4; padding-bottom: 8px; }
              .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 20px 0; }
              .kpi-card { background: #F5F5F4; padding: 20px; border-radius: 12px; text-align: center; }
              .kpi-value { font-size: 28px; font-weight: bold; color: #1A2E05; }
              .kpi-label { font-size: 14px; color: #57534E; margin-top: 5px; }
              .kpi-change { font-size: 12px; margin-top: 5px; }
              .positive { color: #059669; }
              .negative { color: #DC2626; }
              .section { margin: 30px 0; }
              .item { padding: 12px; background: #FAFAF9; margin: 8px 0; border-radius: 8px; border-left: 4px solid #1A2E05; }
              .item-title { font-weight: 600; }
              .item-desc { font-size: 13px; color: #57534E; }
              .summary { background: linear-gradient(135deg, #1A2E05 0%, #2A4208 100%); color: white; padding: 30px; border-radius: 16px; margin: 30px 0; }
              .summary h2 { color: white; border-color: rgba(255,255,255,0.3); }
              .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #E7E5E4; font-size: 12px; color: #78716C; text-align: center; }
              @media print { body { padding: 20px; } }
            </style>
          </head>
          <body>
            <h1>Bedrijfsoverzicht ${currentYear}</h1>
            <p style="color: #57534E;">Gegenereerd op ${new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            
            <div class="summary">
              <h2>Financiële Samenvatting</h2>
              <div class="kpi-grid">
                <div class="kpi-card" style="background: rgba(255,255,255,0.1);">
                  <div class="kpi-value" style="color: white;">${formatCurrency(currentRevenue)}</div>
                  <div class="kpi-label" style="color: rgba(255,255,255,0.7);">Omzet YTD</div>
                  <div class="kpi-change ${revenueGrowth >= 0 ? 'positive' : 'negative'}" style="color: ${revenueGrowth >= 0 ? '#86EFAC' : '#FCA5A5'};">
                    ${revenueGrowth >= 0 ? '+' : ''}${revenueGrowth.toFixed(1)}% vs ${previousYear}
                  </div>
                </div>
                <div class="kpi-card" style="background: rgba(255,255,255,0.1);">
                  <div class="kpi-value" style="color: white;">${formatCurrency(currentCosts)}</div>
                  <div class="kpi-label" style="color: rgba(255,255,255,0.7);">Kosten YTD</div>
                  <div class="kpi-change ${costsChange <= 0 ? 'positive' : 'negative'}" style="color: ${costsChange <= 0 ? '#86EFAC' : '#FCA5A5'};">
                    ${costsChange >= 0 ? '+' : ''}${costsChange.toFixed(1)}% vs ${previousYear}
                  </div>
                </div>
                <div class="kpi-card" style="background: rgba(255,255,255,0.1);">
                  <div class="kpi-value" style="color: white;">${formatCurrency(currentRevenue - currentCosts)}</div>
                  <div class="kpi-label" style="color: rgba(255,255,255,0.7);">Winst YTD</div>
                </div>
              </div>
            </div>

            <h2>⭐ Google Reviews</h2>
            <div class="kpi-grid" style="grid-template-columns: repeat(2, 1fr);">
              <div class="kpi-card">
                <div class="kpi-value">${googleRating.toFixed(1)}</div>
                <div class="kpi-label">Google Cijfer</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-value">${currentYearReviews}</div>
                <div class="kpi-label">Nieuwe Reviews ${currentYear}</div>
                <div class="kpi-change ${reviewsGrowth >= 0 ? 'positive' : 'negative'}">
                  ${reviewsGrowth >= 0 ? '+' : ''}${reviewsGrowth} vs ${previousYear}
                </div>
              </div>
            </div>

            <h2>🎯 Behaalde Doelen (${achievedGoals.length})</h2>
            <div class="section">
              ${achievedGoals.length > 0 ? achievedGoals.map(g => `
                <div class="item">
                  <div class="item-title">${g.title || g.name}</div>
                  ${g.description ? `<div class="item-desc">${g.description}</div>` : ''}
                </div>
              `).join('') : '<p style="color: #78716C;">Nog geen doelen behaald dit jaar</p>'}
            </div>

            <h2>📣 Succesvolle Campagnes (${successfulCampaigns.length})</h2>
            <div class="section">
              ${successfulCampaigns.length > 0 ? successfulCampaigns.map(c => `
                <div class="item">
                  <div class="item-title">${c.name || c.title}</div>
                  ${c.description ? `<div class="item-desc">${c.description}</div>` : ''}
                </div>
              `).join('') : '<p style="color: #78716C;">Nog geen afgeronde campagnes</p>'}
            </div>

            <h2>🎉 Succesvolle Events (${successfulEvents.length})</h2>
            <div class="section">
              ${successfulEvents.length > 0 ? successfulEvents.map(e => `
                <div class="item">
                  <div class="item-title">${e.name || e.title}</div>
                  <div class="item-desc">${e.date ? new Date(e.date).toLocaleDateString('nl-NL') : ''} ${e.guests_attended ? `• ${e.guests_attended} gasten` : ''}</div>
                </div>
              `).join('') : '<p style="color: #78716C;">Nog geen events geweest</p>'}
            </div>

            <h2>🍽️ Arrangementen (${successfulArrangements.length})</h2>
            <div class="section">
              ${successfulArrangements.length > 0 ? successfulArrangements.slice(0, 10).map(a => `
                <div class="item">
                  <div class="item-title">${a.name || a.guest_name || 'Arrangement'}</div>
                  <div class="item-desc">${a.date ? new Date(a.date).toLocaleDateString('nl-NL') : ''} ${a.guests ? `• ${a.guests} gasten` : ''}</div>
                </div>
              `).join('') : '<p style="color: #78716C;">Nog geen arrangementen</p>'}
            </div>

            <h2>💡 Uitgewerkte Ideeën (${completedIdeas.length})</h2>
            <div class="section">
              ${completedIdeas.length > 0 ? completedIdeas.map(i => `
                <div class="item">
                  <div class="item-title">${i.title}</div>
                  ${i.description ? `<div class="item-desc">${i.description}</div>` : ''}
                </div>
              `).join('') : '<p style="color: #78716C;">Nog geen ideeën uitgewerkt</p>'}
            </div>

            <div class="footer">
              <p>Dit overzicht is gegenereerd vanuit Het Horeca Dashboard</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  return (
    <div className="space-y-6" id="bedrijfsoverzicht-content">
      {/* Header with actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Bedrijfsoverzicht {currentYear}
          </h2>
          <p className="text-sm text-stone-600">
            Snel inzicht in de prestaties van je bedrijf
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileText className="w-4 h-4" />
            Exporteer PDF
          </Button>
          <Dialog open={overviewShareDialog} onOpenChange={setOverviewShareDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2">
                <Share2 className="w-4 h-4" />
                Delen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bedrijfsoverzicht Delen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Naam voor deze link</Label>
                  <Input
                    value={overviewShareForm.name}
                    onChange={(e) => setOverviewShareForm({...overviewShareForm, name: e.target.value})}
                    placeholder="Bijv. 'Accountant 2026'"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Wachtwoord</Label>
                  <Input
                    type="password"
                    value={overviewShareForm.password}
                    onChange={(e) => setOverviewShareForm({...overviewShareForm, password: e.target.value})}
                    placeholder="Kies een wachtwoord"
                  />
                </div>
                <Button 
                  className="w-full btn-primary" 
                  onClick={handleCreateShare}
                  disabled={overviewShareLoading}
                >
                  {overviewShareLoading ? 'Aanmaken...' : 'Deelbare Link Aanmaken'}
                </Button>

                {overviewShares.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <p className="text-sm font-medium mb-3">Actieve Links</p>
                    <div className="space-y-2">
                      {overviewShares.map(share => (
                        <div key={share.id || share.share_id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{share.name}</p>
                            <p className="text-xs text-stone-500">{share.views || 0} weergaven</p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleCopyLink(share.share_id)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteShare(share.id || share.share_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Financial Summary - Big Banner */}
      <Card className="card-base bg-gradient-to-br from-[#1A2E05] to-[#2A4208] text-white overflow-hidden">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Euro className="w-5 h-5" />
            Financiële Samenvatting {currentYear}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
              <p className="text-white/70 text-sm">Omzet YTD</p>
              <p className="text-2xl font-bold">{formatCurrency(currentRevenue)}</p>
              <p className={`text-sm ${revenueGrowth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {revenueGrowth >= 0 ? '+' : ''}{revenueGrowth.toFixed(1)}% vs {previousYear}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
              <p className="text-white/70 text-sm">Kosten YTD</p>
              <p className="text-2xl font-bold">{formatCurrency(currentCosts)}</p>
              <p className={`text-sm ${costsChange <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {costsChange >= 0 ? '+' : ''}{costsChange.toFixed(1)}% vs {previousYear}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
              <p className="text-white/70 text-sm">Winst YTD</p>
              <p className="text-2xl font-bold">{formatCurrency(currentProfit)}</p>
              <p className={`text-sm ${profitGrowth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {profitGrowth >= 0 ? '+' : ''}{profitGrowth.toFixed(1)}% vs {previousYear}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
              <p className="text-white/70 text-sm">Winstmarge</p>
              <p className="text-2xl font-bold">
                {currentMargin.toFixed(1)}%
              </p>
              <p className={`text-sm ${marginChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {marginChange >= 0 ? '+' : ''}{marginChange.toFixed(1)}% vs {previousYear}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google Reviews */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Star className="w-5 h-5 text-amber-500" />
            Google Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <div className="flex items-center justify-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`w-5 h-5 ${i <= Math.round(googleRating) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} />
                ))}
              </div>
              <p className="text-3xl font-bold text-amber-600">{googleRating.toFixed(1)}</p>
              <p className="text-sm text-stone-600">Google Cijfer</p>
            </div>
            <div className="text-center p-4 bg-stone-50 rounded-xl">
              <p className="text-3xl font-bold text-[#1A2E05]">{currentYearReviews}</p>
              <p className="text-sm text-stone-600">Reviews {currentYear}</p>
            </div>
            <div className="text-center p-4 bg-stone-50 rounded-xl">
              <p className={`text-3xl font-bold ${reviewsGrowth >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {reviewsGrowth >= 0 ? '+' : ''}{reviewsGrowth}
              </p>
              <p className="text-sm text-stone-600">vs {previousYear}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Achieved Goals */}
        <Card className="card-base">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Behaalde Doelen
              </span>
              <Badge className="bg-emerald-100 text-emerald-700">{achievedGoals.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {achievedGoals.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Nog geen doelen behaald</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {achievedGoals.slice(0, 5).map(goal => (
                  <div key={goal.id} className="flex items-center gap-3 p-2 bg-emerald-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{goal.title || goal.name}</span>
                  </div>
                ))}
                {achievedGoals.length > 5 && (
                  <p className="text-xs text-stone-500 text-center">+ {achievedGoals.length - 5} meer</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Successful Campaigns */}
        <Card className="card-base">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600" />
                Succesvolle Campagnes
              </span>
              <Badge className="bg-purple-100 text-purple-700">{successfulCampaigns.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {successfulCampaigns.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Nog geen afgeronde campagnes</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {successfulCampaigns.slice(0, 5).map(campaign => (
                  <div key={campaign.id} className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg">
                    <Zap className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{campaign.name || campaign.title}</span>
                  </div>
                ))}
                {successfulCampaigns.length > 5 && (
                  <p className="text-xs text-stone-500 text-center">+ {successfulCampaigns.length - 5} meer</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Successful Events - Top 4 by visitors */}
        <Card className="card-base">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Top Events
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500">
                  {successfulEvents.reduce((sum, e) => sum + (e.guests_attended || e.expected_guests || 0), 0)} bezoekers totaal
                </span>
                <Badge className="bg-blue-100 text-blue-700">{successfulEvents.length}</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {successfulEvents.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Nog geen events geweest</p>
            ) : (
              <div className="space-y-2">
                {successfulEvents
                  .sort((a, b) => (b.guests_attended || b.expected_guests || 0) - (a.guests_attended || a.expected_guests || 0))
                  .slice(0, 4)
                  .map((event, index) => (
                  <div key={event.id} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{event.name || event.title}</p>
                      <p className="text-xs text-stone-500">
                        {event.date && new Date(event.date).toLocaleDateString('nl-NL')}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-blue-600">{event.guests_attended || event.expected_guests || 0}</p>
                      <p className="text-xs text-stone-500">bezoekers</p>
                    </div>
                  </div>
                ))}
                {successfulEvents.length > 4 && (
                  <p className="text-xs text-stone-500 text-center pt-2">+ {successfulEvents.length - 4} meer events</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Arrangements - Grouped by type */}
        <Card className="card-base">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
              <span className="flex items-center gap-2">
                <Utensils className="w-5 h-5 text-orange-600" />
                Top Arrangementen
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500">
                  {successfulArrangements.length} boekingen
                </span>
                <Badge className="bg-orange-100 text-orange-700">{successfulArrangements.reduce((sum, a) => sum + (a.actual_guests || a.guests || a.min_guests || 0), 0)} gasten</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {successfulArrangements.length === 0 ? (
              <p className="text-stone-500 text-sm py-4 text-center">Nog geen arrangementen</p>
            ) : (
              <div className="space-y-2">
                {(() => {
                  // Group arrangements by name and sum guests + count bookings
                  const grouped = successfulArrangements.reduce((acc, arr) => {
                    const name = arr.name || 'Overig';
                    if (!acc[name]) {
                      acc[name] = { 
                        name, 
                        totalGuests: 0, 
                        bookings: 0,
                        arrangement_type: arr.arrangement_type 
                      };
                    }
                    acc[name].totalGuests += (arr.actual_guests || arr.guests || arr.min_guests || 0);
                    acc[name].bookings += 1;
                    return acc;
                  }, {});
                  
                  // Convert to array and sort by total guests
                  const sortedArrangements = Object.values(grouped)
                    .sort((a, b) => b.totalGuests - a.totalGuests)
                    .slice(0, 4);
                  
                  return sortedArrangements.map((arr, index) => (
                    <div key={arr.name} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{arr.name}</p>
                        <p className="text-xs text-stone-500">
                          {arr.bookings} {arr.bookings === 1 ? 'boeking' : 'boekingen'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-orange-600">{arr.totalGuests}</p>
                        <p className="text-xs text-stone-500">gasten totaal</p>
                      </div>
                    </div>
                  ));
                })()}
                {Object.keys(successfulArrangements.reduce((acc, arr) => {
                  acc[arr.name || 'Overig'] = true;
                  return acc;
                }, {})).length > 4 && (
                  <p className="text-xs text-stone-500 text-center pt-2">+ meer arrangementen</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ideas */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
            <span className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Uitgewerkte Ideeën
            </span>
            <Badge className="bg-amber-100 text-amber-700">{completedIdeas.length} / {ideas.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ideas.length === 0 ? (
            <p className="text-stone-500 text-sm py-4 text-center">Nog geen ideeën in de inbox</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 bg-stone-100 rounded-full h-3">
                  <div 
                    className="bg-amber-500 h-3 rounded-full transition-all"
                    style={{ width: `${ideas.length > 0 ? (completedIdeas.length / ideas.length) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-sm font-medium">
                  {ideas.length > 0 ? Math.round((completedIdeas.length / ideas.length) * 100) : 0}%
                </span>
              </div>
              {completedIdeas.length > 0 && (
                <div className="grid md:grid-cols-2 gap-2">
                  {completedIdeas.slice(0, 4).map(idea => (
                    <div key={idea.id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span className="text-sm truncate">{idea.title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Financial Analysis Component
const FinancialAnalysis = ({ revenue, costs, selectedYear, setSelectedYear, compareYear, setCompareYear }) => {
  const months = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
  const fullMonths = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
  
  // Get available years from data
  const currentYear = new Date().getFullYear();
  const availableYears = [...new Set([
    currentYear,
    currentYear - 1,
    currentYear - 2,
    ...revenue.map(r => new Date(r.date).getFullYear()),
    ...costs.map(c => new Date(c.date || c.period_start).getFullYear())
  ])].sort((a, b) => b - a);

  // Calculate monthly data for a year
  const getMonthlyData = (year) => {
    const monthlyRevenue = Array(12).fill(0);
    const monthlyInkoop = Array(12).fill(0);
    const monthlyKosten = Array(12).fill(0);
    
    // Sum revenue per month (use amount field, fallback to total)
    revenue.forEach(r => {
      const date = new Date(r.date);
      if (date.getFullYear() === year) {
        monthlyRevenue[date.getMonth()] += r.amount || r.total || 0;
      }
    });
    
    // Sum costs per month
    costs.forEach(c => {
      const date = new Date(c.date || c.period_start);
      if (date.getFullYear() === year) {
        const month = date.getMonth();
        monthlyInkoop[month] += (c.food_cost || 0) + (c.beverage_cost || 0);
        monthlyKosten[month] += (c.staff_cost || 0) + (c.fixed_costs || 0) + (c.marketing_cost || 0) + (c.other_cost || 0);
      }
    });
    
    return { monthlyRevenue, monthlyInkoop, monthlyKosten };
  };

  const currentYearData = getMonthlyData(selectedYear);
  const compareYearData = getMonthlyData(compareYear);

  // Calculate totals
  const calcTotals = (data) => ({
    revenue: data.monthlyRevenue.reduce((a, b) => a + b, 0),
    inkoop: data.monthlyInkoop.reduce((a, b) => a + b, 0),
    kosten: data.monthlyKosten.reduce((a, b) => a + b, 0),
    winst: data.monthlyRevenue.reduce((a, b) => a + b, 0) - data.monthlyInkoop.reduce((a, b) => a + b, 0) - data.monthlyKosten.reduce((a, b) => a + b, 0)
  });

  const currentTotals = calcTotals(currentYearData);
  const compareTotals = calcTotals(compareYearData);

  // Calculate growth percentage
  const calcGrowth = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Prepare chart data
  const chartData = months.map((month, i) => ({
    name: month,
    [`Omzet ${selectedYear}`]: currentYearData.monthlyRevenue[i],
    [`Omzet ${compareYear}`]: compareYearData.monthlyRevenue[i],
    [`Winst ${selectedYear}`]: currentYearData.monthlyRevenue[i] - currentYearData.monthlyInkoop[i] - currentYearData.monthlyKosten[i],
    [`Winst ${compareYear}`]: compareYearData.monthlyRevenue[i] - compareYearData.monthlyInkoop[i] - compareYearData.monthlyKosten[i],
  }));

  // Monthly comparison table data
  const monthlyComparison = months.map((month, i) => {
    const currRevenue = currentYearData.monthlyRevenue[i];
    const prevRevenue = compareYearData.monthlyRevenue[i];
    const currInkoop = currentYearData.monthlyInkoop[i];
    const prevInkoop = compareYearData.monthlyInkoop[i];
    const currKosten = currentYearData.monthlyKosten[i];
    const currWinst = currRevenue - currInkoop - currKosten;
    const prevWinst = prevRevenue - prevInkoop - compareYearData.monthlyKosten[i];
    const winstMarge = currRevenue > 0 ? (currWinst / currRevenue) * 100 : 0;
    const prevWinstMarge = prevRevenue > 0 ? (prevWinst / prevRevenue) * 100 : 0;
    
    return {
      month: fullMonths[i],
      shortMonth: month,
      currRevenue,
      prevRevenue,
      revenueGrowth: calcGrowth(currRevenue, prevRevenue),
      currInkoop,
      prevInkoop,
      inkoopGrowth: calcGrowth(currInkoop, prevInkoop),
      currWinst,
      prevWinst,
      winstGrowth: calcGrowth(currWinst, prevWinst),
      winstMarge,
      prevWinstMarge,
      margeChange: winstMarge - prevWinstMarge
    };
  });

  // Growth indicator component
  const GrowthIndicator = ({ value, inverse = false }) => {
    const isPositive = inverse ? value < 0 : value > 0;
    const isNeutral = Math.abs(value) < 0.5;
    
    if (isNeutral) return <Minus className="w-4 h-4 text-stone-400" />;
    
    return (
      <div className={`flex items-center gap-1 ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
        {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        <span className="text-xs font-medium">{Math.abs(value).toFixed(1)}%</span>
      </div>
    );
  };

  // KPI Card component
  const KPICard = ({ title, icon: Icon, current, previous, format = 'currency', inverse = false }) => {
    const growth = calcGrowth(current, previous);
    const isPositive = inverse ? growth < 0 : growth > 0;
    
    return (
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 text-stone-600">
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{title}</span>
            </div>
            <GrowthIndicator value={growth} inverse={inverse} />
          </div>
          <p className="text-2xl font-bold mt-2" style={{ fontFamily: 'Manrope' }}>
            {format === 'currency' ? formatCurrency(current) : `${current.toFixed(1)}%`}
          </p>
          <p className="text-xs text-stone-500 mt-1">
            {compareYear}: {format === 'currency' ? formatCurrency(previous) : `${previous.toFixed(1)}%`}
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with year selectors */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Financiële Analyse
          </h2>
          <p className="text-sm text-stone-600">
            Vergelijk omzet, inkoop en winstmarge per maand en jaar
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Jaar:</Label>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">vs:</Label>
            <Select value={String(compareYear)} onValueChange={(v) => setCompareYear(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Year-over-Year Growth Card - FIRST */}
      <Card className="card-base bg-gradient-to-br from-[#1A2E05] to-[#2A4208] text-white">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-white" style={{ fontFamily: 'Manrope' }}>
            <BarChart3 className="w-5 h-5" />
            Jaarlijkse Groei {selectedYear} vs {compareYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
              <p className="text-sm text-white/70 mb-2">Omzet Groei</p>
              <div className={`text-3xl font-bold ${calcGrowth(currentTotals.revenue, compareTotals.revenue) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {calcGrowth(currentTotals.revenue, compareTotals.revenue) >= 0 ? '+' : ''}
                {calcGrowth(currentTotals.revenue, compareTotals.revenue).toFixed(1)}%
              </div>
              <p className="text-xs text-white/60 mt-1">
                {formatCurrency(currentTotals.revenue - compareTotals.revenue)}
              </p>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
              <p className="text-sm text-white/70 mb-2">Inkoop Verschil</p>
              <div className={`text-3xl font-bold ${calcGrowth(currentTotals.inkoop, compareTotals.inkoop) <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {calcGrowth(currentTotals.inkoop, compareTotals.inkoop) >= 0 ? '+' : ''}
                {calcGrowth(currentTotals.inkoop, compareTotals.inkoop).toFixed(1)}%
              </div>
              <p className="text-xs text-white/60 mt-1">
                {formatCurrency(currentTotals.inkoop - compareTotals.inkoop)}
              </p>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
              <p className="text-sm text-white/70 mb-2">Winst Groei</p>
              <div className={`text-3xl font-bold ${calcGrowth(currentTotals.winst, compareTotals.winst) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {calcGrowth(currentTotals.winst, compareTotals.winst) >= 0 ? '+' : ''}
                {calcGrowth(currentTotals.winst, compareTotals.winst).toFixed(1)}%
              </div>
              <p className="text-xs text-white/60 mt-1">
                {formatCurrency(currentTotals.winst - compareTotals.winst)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Year Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard 
          title="Totale Omzet" 
          icon={Euro} 
          current={currentTotals.revenue} 
          previous={compareTotals.revenue} 
        />
        <KPICard 
          title="Totale Inkoop" 
          icon={ShoppingCart} 
          current={currentTotals.inkoop} 
          previous={compareTotals.inkoop}
          inverse={true}
        />
        <KPICard 
          title="Totale Winst" 
          icon={Wallet} 
          current={currentTotals.winst} 
          previous={compareTotals.winst} 
        />
        <KPICard 
          title="Winstmarge" 
          icon={Percent} 
          current={currentTotals.revenue > 0 ? (currentTotals.winst / currentTotals.revenue) * 100 : 0} 
          previous={compareTotals.revenue > 0 ? (compareTotals.winst / compareTotals.revenue) * 100 : 0}
          format="percentage"
        />
      </div>

      {/* Revenue Comparison Chart */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Omzet Vergelijking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                />
                <Legend />
                <Bar dataKey={`Omzet ${selectedYear}`} fill="#1A2E05" radius={[4, 4, 0, 0]} />
                <Bar dataKey={`Omzet ${compareYear}`} fill="#A3E635" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Profit Trend Chart */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Wallet className="w-5 h-5 text-[#C2410C]" />
            Winst Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={`Winst ${selectedYear}`} 
                  stroke="#C2410C" 
                  strokeWidth={2}
                  dot={{ fill: '#C2410C', r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey={`Winst ${compareYear}`} 
                  stroke="#D97706" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#D97706', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Comparison Table */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Calendar className="w-5 h-5 text-blue-600" />
            Maandelijks Overzicht {selectedYear} vs {compareYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Maand</TableHead>
                  <TableHead className="text-right">Omzet</TableHead>
                  <TableHead className="text-right">Groei</TableHead>
                  <TableHead className="text-right">Inkoop</TableHead>
                  <TableHead className="text-right">Verschil</TableHead>
                  <TableHead className="text-right">Winst</TableHead>
                  <TableHead className="text-right">Marge</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyComparison.map((row, idx) => (
                  <TableRow key={idx} className="table-row">
                    <TableCell className="font-medium">{row.shortMonth}</TableCell>
                    <TableCell className="text-right">
                      <div>
                        <span className="font-medium">{formatCurrency(row.currRevenue)}</span>
                        <p className="text-xs text-stone-500">{formatCurrency(row.prevRevenue)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <GrowthIndicator value={row.revenueGrowth} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <span className="font-medium">{formatCurrency(row.currInkoop)}</span>
                        <p className="text-xs text-stone-500">{formatCurrency(row.prevInkoop)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <GrowthIndicator value={row.inkoopGrowth} inverse={true} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <span className={`font-medium ${row.currWinst >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {formatCurrency(row.currWinst)}
                        </span>
                        <p className="text-xs text-stone-500">{formatCurrency(row.prevWinst)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`font-medium ${row.winstMarge >= 0 ? '' : 'text-rose-600'}`}>
                          {row.winstMarge.toFixed(1)}%
                        </span>
                        <span className={`text-xs ${row.margeChange >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          ({row.margeChange >= 0 ? '+' : ''}{row.margeChange.toFixed(1)}%)
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-stone-100 font-semibold">
                  <TableCell>TOTAAL</TableCell>
                  <TableCell className="text-right">
                    <div>
                      <span>{formatCurrency(currentTotals.revenue)}</span>
                      <p className="text-xs text-stone-500 font-normal">{formatCurrency(compareTotals.revenue)}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <GrowthIndicator value={calcGrowth(currentTotals.revenue, compareTotals.revenue)} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div>
                      <span>{formatCurrency(currentTotals.inkoop)}</span>
                      <p className="text-xs text-stone-500 font-normal">{formatCurrency(compareTotals.inkoop)}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <GrowthIndicator value={calcGrowth(currentTotals.inkoop, compareTotals.inkoop)} inverse={true} />
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={currentTotals.winst >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {formatCurrency(currentTotals.winst)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {currentTotals.revenue > 0 ? ((currentTotals.winst / currentTotals.revenue) * 100).toFixed(1) : 0}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ActiesEvaluatiePage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [goals, setGoals] = useState([]);
  
  // Financial data
  const [revenue, setRevenue] = useState([]);
  const [costs, setCosts] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [compareYear, setCompareYear] = useState(new Date().getFullYear() - 1);
  
  // Additional data for Bedrijfsoverzicht
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [arrangements, setArrangements] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  
  // Bedrijfsoverzicht share state
  const [overviewShareDialog, setOverviewShareDialog] = useState(false);
  const [overviewShares, setOverviewShares] = useState([]);
  const [overviewShareForm, setOverviewShareForm] = useState({ password: '', name: '' });
  const [overviewShareLoading, setOverviewShareLoading] = useState(false);
  
  // Snelscan state
  const [snelscans, setSnelscans] = useState([]);
  const [snelscanAreas, setSnelscanAreas] = useState([]);
  const [activeSnelscan, setActiveSnelscan] = useState(null);
  const [snelscanLoading, setSnelscanLoading] = useState(false);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [snelscanDialog, setSnelscanDialog] = useState(false);
  const [newSnelscanName, setNewSnelscanName] = useState('Snelscan');
  const [shareDialog, setShareDialog] = useState(false);
  const [shareForm, setShareForm] = useState({ password: '', business_name: '' });
  const [shareLoading, setShareLoading] = useState(false);
  
  // Dialogs
  const [noteDialog, setNoteDialog] = useState(false);
  const [evaluationDialog, setEvaluationDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(false);
  const [signalDialog, setSignalDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  
  const [activeTab, setActiveTab] = useState('overzicht');

  // Forms
  const [noteForm, setNoteForm] = useState({
    content: '',
    week_number: getWeekNumber(new Date()),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    category: 'algemeen'
  });

  const [signalForm, setSignalForm] = useState({
    type: 'handmatig',
    message: '',
    severity: 'info'
  });

  const [evaluationForm, setEvaluationForm] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'week',
    participants: [],
    topics: [],
    agreements: [],
    notes: '',
    rating: 3
  });

  const [actionForm, setActionForm] = useState({
    title: '',
    description: '',
    type: 'verbetering',
    priority: 'normaal',
    assigned_to: '',
    due_date: '',
    goal_id: ''
  });

  const [newTopic, setNewTopic] = useState('');
  const [newAgreement, setNewAgreement] = useState({ text: '', assignee: '' });

  useEffect(() => {
    fetchData();
    fetchSnelscanData();
  }, []);

  const fetchData = async () => {
    try {
      const [
        signalsRes, tasksRes, notesRes, evalRes, empRes, goalsRes, 
        revenueRes, costsRes, campaignsRes, eventsRes, arrangementsRes,
        ideasRes, reviewsRes, settingsRes, sharesRes
      ] = await Promise.all([
        api.get('/signals'),
        api.get('/tasks'),
        api.get('/notes'),
        api.get('/evaluations'),
        api.get('/employees'),
        api.get('/goals'),
        api.get('/revenue'),
        api.get('/costs'),
        api.get('/campaigns').catch(() => ({ data: [] })),
        api.get('/events').catch(() => ({ data: [] })),
        api.get('/arrangements').catch(() => ({ data: [] })),
        api.get('/ideas-inbox').catch(() => ({ data: [] })),
        api.get('/guest-feedback').catch(() => ({ data: [] })),
        api.get('/business-settings').catch(() => ({ data: null })),
        api.get('/overview-shares').catch(() => ({ data: [] }))
      ]);
      setSignals(signalsRes.data);
      setTasks(tasksRes.data);
      setNotes(notesRes.data);
      setEvaluations(evalRes.data);
      setEmployees(empRes.data);
      setGoals(goalsRes.data);
      setRevenue(revenueRes.data || []);
      setCosts(costsRes.data || []);
      setCampaigns(campaignsRes.data || []);
      setEvents(eventsRes.data || []);
      setArrangements(arrangementsRes.data || []);
      setIdeas(ideasRes.data || []);
      setReviews(reviewsRes.data || []);
      setBusinessSettings(settingsRes.data);
      setOverviewShares(sharesRes.data || []);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  // Snelscan data fetching
  const fetchSnelscanData = async () => {
    try {
      const [areasRes, scansRes] = await Promise.all([
        api.get('/snelscan/areas'),
        api.get('/snelscan')
      ]);
      setSnelscanAreas(areasRes.data);
      setSnelscans(scansRes.data);
      
      // Auto-select the most recent incomplete scan or the latest one
      if (scansRes.data.length > 0) {
        const incompleteScan = scansRes.data.find(s => !s.completed);
        setActiveSnelscan(incompleteScan || scansRes.data[0]);
      }
    } catch (error) {
      console.error('Fout bij laden snelscan data', error);
    }
  };

  // Snelscan handlers
  const handleCreateSnelscan = async () => {
    try {
      setSnelscanLoading(true);
      const res = await api.post('/snelscan', { name: newSnelscanName });
      toast.success('Nieuwe snelscan gestart');
      setActiveSnelscan(res.data);
      setSnelscans([res.data, ...snelscans]);
      setSnelscanDialog(false);
      setNewSnelscanName('Snelscan');
    } catch (error) {
      toast.error('Fout bij aanmaken snelscan');
    } finally {
      setSnelscanLoading(false);
    }
  };

  const handleUpdateSnelscanArea = async (areaId, updates) => {
    if (!activeSnelscan) return;
    
    try {
      const updatedAreas = activeSnelscan.areas.map(area => 
        area.area_id === areaId ? { ...area, ...updates } : area
      );
      
      const res = await api.put(`/snelscan/${activeSnelscan.id}`, { areas: updatedAreas });
      setActiveSnelscan(res.data);
      
      // Update in list
      setSnelscans(snelscans.map(s => s.id === res.data.id ? res.data : s));
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleToggleSnelscanItem = (areaId, item) => {
    const area = activeSnelscan?.areas?.find(a => a.area_id === areaId);
    if (!area) return;
    
    const checkedItems = area.checked_items || [];
    const newCheckedItems = checkedItems.includes(item)
      ? checkedItems.filter(i => i !== item)
      : [...checkedItems, item];
    
    handleUpdateSnelscanArea(areaId, { checked_items: newCheckedItems });
  };

  const handleSetAreaStatus = (areaId, status) => {
    handleUpdateSnelscanArea(areaId, { status });
  };

  const handleSetAreaNotes = (areaId, notes) => {
    handleUpdateSnelscanArea(areaId, { notes });
  };

  const handleDeleteSnelscan = async (scanId) => {
    if (!window.confirm('Snelscan verwijderen?')) return;
    try {
      await api.delete(`/snelscan/${scanId}`);
      toast.success('Snelscan verwijderd');
      setSnelscans(snelscans.filter(s => s.id !== scanId));
      if (activeSnelscan?.id === scanId) {
        const remaining = snelscans.filter(s => s.id !== scanId);
        setActiveSnelscan(remaining.length > 0 ? remaining[0] : null);
      }
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleShareSnelscan = async (e) => {
    e.preventDefault();
    if (!activeSnelscan) return;
    
    setShareLoading(true);
    try {
      const res = await api.post(`/snelscan/${activeSnelscan.id}/share`, shareForm);
      const shareUrl = `${window.location.origin}/snelscan/${res.data.share_id}`;
      
      // Update local state
      setActiveSnelscan({ ...activeSnelscan, is_shared: true, share_id: res.data.share_id });
      setSnelscans(snelscans.map(s => s.id === activeSnelscan.id ? { ...s, is_shared: true, share_id: res.data.share_id } : s));
      
      // Copy to clipboard with fallback
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch (clipErr) {
        const input = document.createElement('input');
        input.value = shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      toast.success('Link gekopieerd naar klembord!');
      setShareDialog(false);
      setShareForm({ password: '', business_name: '' });
    } catch (error) {
      toast.error('Fout bij delen');
    } finally {
      setShareLoading(false);
    }
  };

  const handleUnshareSnelscan = async () => {
    if (!activeSnelscan) return;
    if (!window.confirm('Delen uitschakelen? De link werkt dan niet meer.')) return;
    
    try {
      await api.post(`/snelscan/${activeSnelscan.id}/unshare`);
      setActiveSnelscan({ ...activeSnelscan, is_shared: false, share_id: null });
      setSnelscans(snelscans.map(s => s.id === activeSnelscan.id ? { ...s, is_shared: false, share_id: null } : s));
      toast.success('Delen uitgeschakeld');
    } catch (error) {
      toast.error('Fout bij uitschakelen');
    }
  };

  const copyShareLink = async () => {
    if (!activeSnelscan?.share_id) return;
    const shareUrl = `${window.location.origin}/snelscan/${activeSnelscan.share_id}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link gekopieerd!');
    } catch (err) {
      // Fallback: show link in prompt
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Link gekopieerd!');
    }
  };

  const toggleAreaExpanded = (areaId) => {
    setExpandedAreas(prev => ({ ...prev, [areaId]: !prev[areaId] }));
  };

  const getAreaStatus = (areaId) => {
    const area = activeSnelscan?.areas?.find(a => a.area_id === areaId);
    return area?.status || 'niet_beoordeeld';
  };

  const getAreaCheckedItems = (areaId) => {
    const area = activeSnelscan?.areas?.find(a => a.area_id === areaId);
    return area?.checked_items || [];
  };

  const getAreaNotes = (areaId) => {
    const area = activeSnelscan?.areas?.find(a => a.area_id === areaId);
    return area?.notes || '';
  };

  const statusConfig = {
    niet_beoordeeld: { label: 'Niet beoordeeld', bg: 'bg-stone-100', text: 'text-stone-600', icon: '⚪' },
    goed: { label: 'Goed', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: '🟢' },
    aandacht: { label: 'Aandacht', bg: 'bg-amber-100', text: 'text-amber-700', icon: '🟡' },
    kritiek: { label: 'Kritiek', bg: 'bg-rose-100', text: 'text-rose-700', icon: '🔴' }
  };

  // Handlers
  const handleMarkSignalRead = async (id) => {
    try {
      await api.put(`/signals/${id}/read`);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleAddSignal = async (e) => {
    e.preventDefault();
    try {
      await api.post('/signals', signalForm);
      toast.success('Signaal toegevoegd');
      setSignalDialog(false);
      setSignalForm({
        type: 'handmatig',
        message: '',
        severity: 'info'
      });
      fetchData();
    } catch (error) {
      toast.error('Fout bij toevoegen signaal');
    }
  };

  const handleDeleteSignal = async (id) => {
    if (!window.confirm('Signaal verwijderen?')) return;
    try {
      await api.delete(`/signals/${id}`);
      toast.success('Signaal verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdateTaskStatus = async (id, status) => {
    try {
      await api.put(`/tasks/${id}/status`, null, { params: { status } });
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      await api.post('/notes', noteForm);
      toast.success('Notitie opgeslagen');
      setNoteDialog(false);
      setNoteForm({
        content: '',
        week_number: getWeekNumber(new Date()),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        category: 'algemeen'
      });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddEvaluation = async (e) => {
    e.preventDefault();
    try {
      await api.post('/evaluations', evaluationForm);
      toast.success('Evaluatie opgeslagen');
      setEvaluationDialog(false);
      setEvaluationForm({
        date: new Date().toISOString().split('T')[0],
        type: 'week',
        participants: [],
        topics: [],
        agreements: [],
        notes: '',
        rating: 3
      });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddAction = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tasks', {
        ...actionForm,
        status: 'open',
        goal_id: actionForm.goal_id || null,
        assigned_to: actionForm.assigned_to || null
      });
      toast.success('Actie toegevoegd');
      setActionDialog(false);
      setActionForm({
        title: '',
        description: '',
        type: 'verbetering',
        priority: 'normaal',
        assigned_to: '',
        due_date: '',
        goal_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteNote = async (id) => {
    if (!window.confirm('Notitie verwijderen?')) return;
    try {
      await api.delete(`/notes/${id}`);
      toast.success('Notitie verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleDeleteEvaluation = async (id) => {
    if (!window.confirm('Evaluatie verwijderen?')) return;
    try {
      await api.delete(`/evaluations/${id}`);
      toast.success('Evaluatie verwijderd');
      setDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const addTopic = () => {
    if (!newTopic.trim()) return;
    setEvaluationForm({
      ...evaluationForm,
      topics: [...evaluationForm.topics, { text: newTopic, discussed: false }]
    });
    setNewTopic('');
  };

  const addAgreement = () => {
    if (!newAgreement.text.trim()) return;
    setEvaluationForm({
      ...evaluationForm,
      agreements: [...evaluationForm.agreements, { ...newAgreement, completed: false }]
    });
    setNewAgreement({ text: '', assignee: '' });
  };

  const openEvaluationDetail = (evalItem) => {
    setSelectedEvaluation(evalItem);
    setDetailDialog(true);
  };

  // Helpers
  const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || 'Niet toegewezen';

  // Stats
  const unreadSignals = signals.filter(s => !s.is_read).length;
  const criticalSignals = signals.filter(s => s.severity === 'error' && !s.is_read).length;
  const openTasks = tasks.filter(t => t.status !== 'voltooid').length;
  const highPriorityTasks = tasks.filter(t => t.priority === 'hoog' && t.status !== 'voltooid').length;
  const thisWeekNotes = notes.filter(n => n.week_number === getWeekNumber(new Date())).length;
  const thisMonthEvaluations = evaluations.filter(e => {
    const evalDate = new Date(e.date);
    const now = new Date();
    return evalDate.getMonth() === now.getMonth() && evalDate.getFullYear() === now.getFullYear();
  }).length;
  const completedTasksThisWeek = tasks.filter(t => {
    if (t.status !== 'voltooid') return false;
    // Simple check - would need updated_at field for accuracy
    return true;
  }).length;

  const severityColors = {
    info: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200' },
    warning: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    error: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' }
  };

  const severityIcons = {
    info: TrendingUp,
    warning: AlertTriangle,
    error: TrendingDown
  };

  const priorityConfig = {
    laag: { bg: 'bg-sky-100', text: 'text-sky-700' },
    normaal: { bg: 'bg-amber-100', text: 'text-amber-700' },
    hoog: { bg: 'bg-rose-100', text: 'text-rose-700' }
  };

  const categoryConfig = {
    algemeen: { icon: FileText, color: 'text-stone-600' },
    personeel: { icon: Users, color: 'text-blue-600' },
    kwaliteit: { icon: Star, color: 'text-amber-600' },
    omzet: { icon: TrendingUp, color: 'text-emerald-600' },
    klacht: { icon: AlertTriangle, color: 'text-rose-600' }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="acties-evaluatie-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Acties & Evaluatie
          </h1>
          <p className="text-[#57534E] mt-1">
            Signalen, acties, notities en team evaluaties beheren
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Signal Dialog */}
          <Dialog open={signalDialog} onOpenChange={setSignalDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary border-amber-300 text-amber-700 hover:bg-amber-50">
                <Bell className="w-4 h-4 mr-2" />
                Signaal
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuw signaal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddSignal} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Bericht *</Label>
                  <Textarea
                    value={signalForm.message}
                    onChange={(e) => setSignalForm({ ...signalForm, message: e.target.value })}
                    placeholder="Beschrijf het signaal of de waarschuwing..."
                    rows={3}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={signalForm.type}
                      onValueChange={(value) => setSignalForm({ ...signalForm, type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="handmatig">📝 Handmatig</SelectItem>
                        <SelectItem value="herinnering">⏰ Herinnering</SelectItem>
                        <SelectItem value="waarschuwing">⚠️ Waarschuwing</SelectItem>
                        <SelectItem value="urgent">🚨 Urgent</SelectItem>
                        <SelectItem value="info">ℹ️ Informatie</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ernst</Label>
                    <Select
                      value={signalForm.severity}
                      onValueChange={(value) => setSignalForm({ ...signalForm, severity: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">🟢 Info</SelectItem>
                        <SelectItem value="warning">🟡 Waarschuwing</SelectItem>
                        <SelectItem value="error">🔴 Kritiek</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg text-sm text-[#57534E]">
                  <p className="font-medium mb-1">💡 Tip</p>
                  <p>Gebruik signalen om belangrijke zaken onder de aandacht te brengen van jezelf of je team.</p>
                </div>
                <Button type="submit" className="w-full btn-primary">Toevoegen</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={actionDialog} onOpenChange={setActionDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary">
                <Zap className="w-4 h-4 mr-2" />
                Actie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuwe actie</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddAction} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Titel *</Label>
                  <Input
                    value={actionForm.title}
                    onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })}
                    placeholder="Wat moet er gebeuren?"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={actionForm.type}
                      onValueChange={(value) => setActionForm({ ...actionForm, type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="verbetering">🔧 Verbetering</SelectItem>
                        <SelectItem value="onderzoek">🔍 Onderzoek</SelectItem>
                        <SelectItem value="follow-up">📞 Follow-up</SelectItem>
                        <SelectItem value="training">📚 Training</SelectItem>
                        <SelectItem value="overig">📋 Overig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prioriteit</Label>
                    <Select
                      value={actionForm.priority}
                      onValueChange={(value) => setActionForm({ ...actionForm, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laag">🟢 Laag</SelectItem>
                        <SelectItem value="normaal">🟡 Normaal</SelectItem>
                        <SelectItem value="hoog">🔴 Hoog</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Toegewezen aan</Label>
                    <Select
                      value={actionForm.assigned_to || "none"}
                      onValueChange={(value) => setActionForm({ ...actionForm, assigned_to: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Niemand</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Deadline</Label>
                    <Input
                      type="date"
                      value={actionForm.due_date}
                      onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Gekoppeld doel</Label>
                  <Select
                    value={actionForm.goal_id || "none"}
                    onValueChange={(value) => setActionForm({ ...actionForm, goal_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer doel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen doel</SelectItem>
                      {goals.filter(g => g.status !== 'voltooid').map(goal => (
                        <SelectItem key={goal.id} value={goal.id}>{goal.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Beschrijving</Label>
                  <Textarea
                    value={actionForm.description}
                    onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                    placeholder="Extra details..."
                  />
                </div>
                <Button type="submit" className="w-full btn-primary">Toevoegen</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary">
                <FileText className="w-4 h-4 mr-2" />
                Notitie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuwe notitie</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddNote} className="space-y-4 pt-4">
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Week</Label>
                    <Input
                      type="number"
                      value={noteForm.week_number}
                      onChange={(e) => setNoteForm({ ...noteForm, week_number: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Maand</Label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={noteForm.month}
                      onChange={(e) => setNoteForm({ ...noteForm, month: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Jaar</Label>
                    <Input
                      type="number"
                      value={noteForm.year}
                      onChange={(e) => setNoteForm({ ...noteForm, year: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select
                      value={noteForm.category}
                      onValueChange={(value) => setNoteForm({ ...noteForm, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="algemeen">📋 Algemeen</SelectItem>
                        <SelectItem value="personeel">👥 Personeel</SelectItem>
                        <SelectItem value="kwaliteit">⭐ Kwaliteit</SelectItem>
                        <SelectItem value="omzet">📈 Omzet</SelectItem>
                        <SelectItem value="klacht">⚠️ Klacht</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Inhoud *</Label>
                  <Textarea
                    value={noteForm.content}
                    onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                    rows={5}
                    required
                    placeholder="Schrijf hier je notitie..."
                  />
                </div>
                <Button type="submit" className="w-full btn-primary">Opslaan</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={evaluationDialog} onOpenChange={setEvaluationDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-evaluation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Evaluatie
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Nieuwe evaluatie</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddEvaluation} className="space-y-4 pt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={evaluationForm.date}
                      onChange={(e) => setEvaluationForm({ ...evaluationForm, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={evaluationForm.type}
                      onValueChange={(value) => setEvaluationForm({ ...evaluationForm, type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="week">Week evaluatie</SelectItem>
                        <SelectItem value="maand">Maand evaluatie</SelectItem>
                        <SelectItem value="kwartaal">Kwartaal evaluatie</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Algemene beoordeling</Label>
                    <div className="flex items-center gap-1 pt-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setEvaluationForm({ ...evaluationForm, rating: star })}
                          className="focus:outline-none"
                        >
                          <Star 
                            className={`w-6 h-6 ${star <= evaluationForm.rating ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Topics */}
                <div className="space-y-2">
                  <Label>Bespreekpunten</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newTopic}
                      onChange={(e) => setNewTopic(e.target.value)}
                      placeholder="Voeg onderwerp toe"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())}
                    />
                    <Button type="button" variant="outline" onClick={addTopic}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-1 mt-2">
                    {evaluationForm.topics.map((topic, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-stone-50 rounded text-sm">
                        <MessageSquare className="w-4 h-4 text-[#C2410C]" />
                        <span className="flex-1">{topic.text}</span>
                        <button 
                          type="button"
                          onClick={() => setEvaluationForm({
                            ...evaluationForm,
                            topics: evaluationForm.topics.filter((_, idx) => idx !== i)
                          })}
                          className="text-rose-500 hover:text-rose-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Agreements */}
                <div className="space-y-2">
                  <Label>Afspraken / Actiepunten</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newAgreement.text}
                      onChange={(e) => setNewAgreement({ ...newAgreement, text: e.target.value })}
                      placeholder="Afspraak"
                      className="flex-1"
                    />
                    <Select
                      value={newAgreement.assignee || "none"}
                      onValueChange={(value) => setNewAgreement({ ...newAgreement, assignee: value === "none" ? "" : value })}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Wie" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Niemand</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" onClick={addAgreement}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-1 mt-2">
                    {evaluationForm.agreements.map((agr, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-stone-50 rounded text-sm">
                        <CheckSquare className="w-4 h-4 text-emerald-600" />
                        <span className="flex-1">{agr.text}</span>
                        {agr.assignee && (
                          <Badge variant="outline">{getEmployeeName(agr.assignee)}</Badge>
                        )}
                        <button 
                          type="button"
                          onClick={() => setEvaluationForm({
                            ...evaluationForm,
                            agreements: evaluationForm.agreements.filter((_, idx) => idx !== i)
                          })}
                          className="text-rose-500 hover:text-rose-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notities</Label>
                  <Textarea
                    value={evaluationForm.notes}
                    onChange={(e) => setEvaluationForm({ ...evaluationForm, notes: e.target.value })}
                    rows={3}
                    placeholder="Algemene opmerkingen..."
                  />
                </div>
                <Button type="submit" className="w-full btn-primary">Opslaan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 flex-wrap">
          <TabsTrigger value="overzicht" className="data-[state=active]:bg-white">
            <Layers className="w-4 h-4 mr-2" />
            Overzicht
          </TabsTrigger>
          <TabsTrigger value="financieel" className="data-[state=active]:bg-white">
            <Euro className="w-4 h-4 mr-2" />
            Financieel
          </TabsTrigger>
          <TabsTrigger value="snelscan" className="data-[state=active]:bg-white">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Snelscan
          </TabsTrigger>
          <TabsTrigger value="signals" className="data-[state=active]:bg-white relative">
            <Bell className="w-4 h-4 mr-2" />
            Signalen
            {unreadSignals > 0 && (
              <span className="ml-2 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadSignals}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tasks" className="data-[state=active]:bg-white">
            <ClipboardList className="w-4 h-4 mr-2" />
            Acties ({openTasks})
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Notities
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Evaluaties
          </TabsTrigger>
          <TabsTrigger value="bedrijfsoverzicht" className="data-[state=active]:bg-white">
            <Building className="w-4 h-4 mr-2" />
            Bedrijfsoverzicht
          </TabsTrigger>
        </TabsList>

        {/* Overzicht Tab */}
        <TabsContent value="overzicht" className="mt-6 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="kpi-tile cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('signals')}>
              <p className="kpi-label">Signalen</p>
              <p className={`kpi-value ${unreadSignals > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {unreadSignals}
              </p>
              <p className="text-xs text-[#57534E]">ongelezen</p>
            </div>
            <div className="kpi-tile cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('signals')}>
              <p className="kpi-label">Kritiek</p>
              <p className={`kpi-value ${criticalSignals > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {criticalSignals}
              </p>
              <p className="text-xs text-[#57534E]">actie vereist</p>
            </div>
            <div className="kpi-tile cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('tasks')}>
              <p className="kpi-label">Open Acties</p>
              <p className="kpi-value">{openTasks}</p>
              <p className="text-xs text-[#57534E]">{highPriorityTasks} hoog</p>
            </div>
            <div className="kpi-tile cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('tasks')}>
              <p className="kpi-label">Voltooid</p>
              <p className="kpi-value text-emerald-600">{tasks.filter(t => t.status === 'voltooid').length}</p>
              <p className="text-xs text-[#57534E]">acties</p>
            </div>
            <div className="kpi-tile cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('notes')}>
              <p className="kpi-label">Notities</p>
              <p className="kpi-value">{thisWeekNotes}</p>
              <p className="text-xs text-[#57534E]">deze week</p>
            </div>
            <div className="kpi-tile cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('evaluations')}>
              <p className="kpi-label">Evaluaties</p>
              <p className="kpi-value">{thisMonthEvaluations}</p>
              <p className="text-xs text-[#57534E]">deze maand</p>
            </div>
          </div>

          {/* Quick Actions & Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Urgent Signals */}
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Aandachtspunten
                </CardTitle>
                <Button variant="link" className="text-[#C2410C]" onClick={() => setActiveTab('signals')}>
                  Alle signalen <ChevronRight className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {signals.filter(s => !s.is_read).slice(0, 4).length === 0 ? (
                  <div className="text-center py-6 text-[#57534E]">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                    <p>Geen openstaande signalen!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {signals.filter(s => !s.is_read).slice(0, 4).map(signal => {
                      const Icon = severityIcons[signal.severity];
                      const colors = severityColors[signal.severity];
                      return (
                        <div 
                          key={signal.id} 
                          className={`flex items-start gap-3 p-3 rounded-lg border ${colors.bg} ${colors.border}`}
                        >
                          <Icon className={`w-5 h-5 ${colors.text} shrink-0 mt-0.5`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium line-clamp-2">{signal.message}</p>
                            <p className="text-xs text-[#57534E] mt-1">{formatDate(signal.created_at)}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="shrink-0"
                            onClick={() => handleMarkSignalRead(signal.id)}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* High Priority Tasks */}
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5 text-rose-500" />
                  Prioritaire Acties
                </CardTitle>
                <Button variant="link" className="text-[#C2410C]" onClick={() => setActiveTab('tasks')}>
                  Alle acties <ChevronRight className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {tasks.filter(t => t.status !== 'voltooid').slice(0, 4).length === 0 ? (
                  <div className="text-center py-6 text-[#57534E]">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                    <p>Alle acties voltooid!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks
                      .filter(t => t.status !== 'voltooid')
                      .sort((a, b) => {
                        const order = { hoog: 0, normaal: 1, laag: 2 };
                        return order[a.priority] - order[b.priority];
                      })
                      .slice(0, 4)
                      .map(task => (
                        <div 
                          key={task.id} 
                          className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg"
                        >
                          <Checkbox
                            checked={task.status === 'voltooid'}
                            onCheckedChange={(checked) => 
                              handleUpdateTaskStatus(task.id, checked ? 'voltooid' : 'open')
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{task.title}</p>
                            {task.due_date && (
                              <p className="text-xs text-[#57534E] flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(task.due_date)}
                              </p>
                            )}
                          </div>
                          <Badge className={`${priorityConfig[task.priority]?.bg} ${priorityConfig[task.priority]?.text}`}>
                            {task.priority}
                          </Badge>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Evaluations */}
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recente Evaluaties</CardTitle>
              <Button variant="link" className="text-[#C2410C]" onClick={() => setActiveTab('evaluations')}>
                Alle evaluaties <ChevronRight className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {evaluations.length === 0 ? (
                <div className="text-center py-8 text-[#57534E]">
                  <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Nog geen evaluaties</p>
                  <Button className="mt-4" variant="outline" onClick={() => setEvaluationDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Eerste evaluatie toevoegen
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {evaluations.slice(0, 3).map(evalItem => (
                    <div 
                      key={evalItem.id}
                      className="p-4 bg-stone-50 rounded-lg cursor-pointer hover:bg-stone-100 transition-colors"
                      onClick={() => openEvaluationDetail(evalItem)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={evalItem.type === 'week' ? 'bg-sky-100 text-sky-700' : evalItem.type === 'maand' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}>
                          {evalItem.type}
                        </Badge>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star 
                              key={star}
                              className={`w-4 h-4 ${star <= (evalItem.rating || 3) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} 
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-[#57534E]">{formatDate(evalItem.date)}</p>
                      <p className="text-sm mt-2">
                        {evalItem.topics?.length || 0} punten • {evalItem.agreements?.length || 0} afspraken
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financieel Tab */}
        <TabsContent value="financieel" className="mt-6 space-y-6">
          <FinancialAnalysis 
            revenue={revenue} 
            costs={costs} 
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            compareYear={compareYear}
            setCompareYear={setCompareYear}
          />
        </TabsContent>

        {/* Snelscan Tab */}
        <TabsContent value="snelscan" className="mt-6 space-y-6" data-testid="snelscan-tab">
          {/* Header with actions */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-[#1C1917]">Snelscan Horeca Onderneming</h2>
              <p className="text-[#57534E] text-sm mt-1">
                Beoordeel je bedrijf op 10 kerngebieden in 5-10 minuten
              </p>
            </div>
            <div className="flex items-center gap-3">
              {snelscans.length > 0 && (
                <Select
                  value={activeSnelscan?.id || ''}
                  onValueChange={(value) => {
                    const scan = snelscans.find(s => s.id === value);
                    setActiveSnelscan(scan);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Selecteer scan" />
                  </SelectTrigger>
                  <SelectContent>
                    {snelscans.map(scan => (
                      <SelectItem key={scan.id} value={scan.id}>
                        {scan.name} - {formatDate(scan.date)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Dialog open={snelscanDialog} onOpenChange={setSnelscanDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="new-snelscan-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuwe Scan
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe Snelscan</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam</Label>
                      <Input
                        value={newSnelscanName}
                        onChange={(e) => setNewSnelscanName(e.target.value)}
                        placeholder="Snelscan december 2025"
                      />
                    </div>
                    <p className="text-sm text-[#57534E]">
                      Start een nieuwe scan om de huidige staat van je onderneming te evalueren.
                    </p>
                    <Button 
                      className="w-full btn-primary" 
                      onClick={handleCreateSnelscan}
                      disabled={snelscanLoading}
                    >
                      {snelscanLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ClipboardCheck className="w-4 h-4 mr-2" />
                      )}
                      Start Scan
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              {/* Share Dialog */}
              {activeSnelscan && !activeSnelscan.is_shared && (
                <Dialog open={shareDialog} onOpenChange={setShareDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                      <Share2 className="w-4 h-4 mr-2" />
                      Delen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-[#C2410C]" />
                        Snelscan Delen
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleShareSnelscan} className="space-y-4 pt-4">
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-start gap-3">
                          <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-800">
                            De snelscan wordt beveiligd met een wachtwoord. Deel het wachtwoord alleen met vertrouwde adviseurs of coaches.
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Bedrijfsnaam (zichtbaar in rapport)</Label>
                        <Input
                          value={shareForm.business_name}
                          onChange={(e) => setShareForm({ ...shareForm, business_name: e.target.value })}
                          placeholder="Mijn Horecazaak"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Wachtwoord *</Label>
                        <Input
                          type="password"
                          value={shareForm.password}
                          onChange={(e) => setShareForm({ ...shareForm, password: e.target.value })}
                          placeholder="Kies een sterk wachtwoord"
                          required
                          minLength={4}
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full btn-primary"
                        disabled={shareLoading || !shareForm.password}
                      >
                        {shareLoading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Link className="w-4 h-4 mr-2" />
                        )}
                        Genereer Deellink
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
              
              {/* Already Shared Controls */}
              {activeSnelscan?.is_shared && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                    <Link className="w-3 h-3" />
                    Gedeeld
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={copyShareLink}
                    className="text-[#57534E]"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Kopieer Link
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(`/snelscan/${activeSnelscan.share_id}`, '_blank')}
                    className="text-[#57534E]"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Bekijk
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleUnshareSnelscan}
                    className="text-rose-600 hover:bg-rose-50"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* No scan selected state */}
          {!activeSnelscan ? (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                <h3 className="text-lg font-medium mb-2">Geen snelscan geselecteerd</h3>
                <p className="text-[#57534E] mb-4">
                  Start een nieuwe snelscan om je onderneming te evalueren
                </p>
                <Button className="btn-primary" onClick={() => setSnelscanDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Start Eerste Scan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Overall Progress */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-base col-span-1 md:col-span-2">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium">Voortgang</h3>
                      <Badge className={activeSnelscan.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {activeSnelscan.completed ? 'Voltooid' : 'In uitvoering'}
                      </Badge>
                    </div>
                    <Progress 
                      value={activeSnelscan.areas?.filter(a => a.status !== 'niet_beoordeeld').length / 10 * 100 || 0} 
                      className="h-3 mb-2"
                    />
                    <p className="text-sm text-[#57534E]">
                      {activeSnelscan.areas?.filter(a => a.status !== 'niet_beoordeeld').length || 0} van 10 gebieden beoordeeld
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="card-base">
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-[#57534E] mb-1">Score</p>
                    <p className={`text-3xl font-bold ${
                      activeSnelscan.overall_score >= 70 ? 'text-emerald-600' :
                      activeSnelscan.overall_score >= 40 ? 'text-amber-600' :
                      activeSnelscan.overall_score > 0 ? 'text-rose-600' : 'text-stone-400'
                    }`}>
                      {activeSnelscan.overall_score != null ? `${Math.round(activeSnelscan.overall_score)}%` : '-'}
                    </p>
                  </CardContent>
                </Card>

                <Card className="card-base">
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-[#57534E] mb-1">Status Overzicht</p>
                    <div className="flex justify-center gap-3 mt-2">
                      <span title="Goed">🟢 {activeSnelscan.areas?.filter(a => a.status === 'goed').length || 0}</span>
                      <span title="Aandacht">🟡 {activeSnelscan.areas?.filter(a => a.status === 'aandacht').length || 0}</span>
                      <span title="Kritiek">🔴 {activeSnelscan.areas?.filter(a => a.status === 'kritiek').length || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Scan Areas */}
              <div className="space-y-4">
                {snelscanAreas.map((area, index) => {
                  const currentStatus = getAreaStatus(area.id);
                  const checkedItems = getAreaCheckedItems(area.id);
                  const areaNotes = getAreaNotes(area.id);
                  const isExpanded = expandedAreas[area.id];
                  const config = statusConfig[currentStatus];
                  
                  return (
                    <Card 
                      key={area.id} 
                      className={`card-base border-l-4 ${
                        currentStatus === 'goed' ? 'border-l-emerald-500' :
                        currentStatus === 'aandacht' ? 'border-l-amber-500' :
                        currentStatus === 'kritiek' ? 'border-l-rose-500' :
                        'border-l-stone-300'
                      }`}
                      data-testid={`snelscan-area-${area.id}`}
                    >
                      {/* Area Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                        onClick={() => toggleAreaExpanded(area.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{area.icon}</span>
                            <div>
                              <h3 className="font-semibold text-[#1C1917]">
                                {index + 1}. {area.title}
                              </h3>
                              <p className="text-sm text-[#57534E]">
                                {checkedItems.length} items gecontroleerd
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${config.bg} ${config.text}`}>
                              {config.icon} {config.label}
                            </Badge>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-[#57534E]" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-[#57534E]" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <CardContent className="pt-0 border-t">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                            {/* Waar op letten */}
                            <div>
                              <h4 className="font-medium text-emerald-700 mb-3 flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Waar op letten
                              </h4>
                              <div className="space-y-2">
                                {area.waar_op_letten.map((item, i) => (
                                  <div 
                                    key={i}
                                    className="flex items-start gap-3 p-2 rounded hover:bg-stone-50 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleSnelscanItem(area.id, `check_${i}`);
                                    }}
                                  >
                                    <Checkbox 
                                      checked={checkedItems.includes(`check_${i}`)}
                                      className="mt-0.5"
                                    />
                                    <span className={`text-sm ${checkedItems.includes(`check_${i}`) ? 'text-[#57534E] line-through' : ''}`}>
                                      {item}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Red Flags */}
                            <div>
                              <h4 className="font-medium text-rose-700 mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Snel herkennen (Red Flags)
                              </h4>
                              <div className="space-y-2">
                                {area.red_flags.map((flag, i) => (
                                  <div 
                                    key={i}
                                    className="flex items-start gap-3 p-2 rounded hover:bg-rose-50 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleSnelscanItem(area.id, `flag_${i}`);
                                    }}
                                  >
                                    <Checkbox 
                                      checked={checkedItems.includes(`flag_${i}`)}
                                      className="mt-0.5 data-[state=checked]:bg-rose-600"
                                    />
                                    <span className={`text-sm ${checkedItems.includes(`flag_${i}`) ? 'text-rose-600 font-medium' : ''}`}>
                                      {flag}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Status Selection & Notes */}
                          <div className="mt-6 pt-4 border-t space-y-4">
                            <div>
                              <Label className="text-sm font-medium mb-2 block">Beoordeling</Label>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(statusConfig).filter(([key]) => key !== 'niet_beoordeeld').map(([key, conf]) => (
                                  <Button
                                    key={key}
                                    variant={currentStatus === key ? 'default' : 'outline'}
                                    size="sm"
                                    className={currentStatus === key ? `${conf.bg} ${conf.text} border-2` : ''}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetAreaStatus(area.id, key);
                                    }}
                                  >
                                    {conf.icon} {conf.label}
                                  </Button>
                                ))}
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-sm font-medium mb-2 block">Notities voor dit gebied</Label>
                              <Textarea
                                value={areaNotes}
                                onChange={(e) => handleSetAreaNotes(area.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Voeg je observaties of actiepunten toe..."
                                rows={2}
                              />
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>

              {/* Summary Notes & Actions */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#C2410C]" />
                    Samenvattende Notities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={activeSnelscan.summary_notes || ''}
                    onChange={(e) => {
                      api.put(`/snelscan/${activeSnelscan.id}`, { summary_notes: e.target.value })
                        .then(res => setActiveSnelscan(res.data))
                        .catch(() => toast.error('Fout bij opslaan'));
                    }}
                    placeholder="Voeg hier je algemene conclusies, prioriteiten en vervolgacties toe..."
                    rows={4}
                  />
                  <div className="flex justify-between items-center mt-4">
                    <p className="text-sm text-[#57534E]">
                      Laatst bijgewerkt: {formatDate(activeSnelscan.updated_at)}
                    </p>
                    <Button 
                      variant="outline" 
                      className="text-rose-600 border-rose-300"
                      onClick={() => handleDeleteSnelscan(activeSnelscan.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Scan Verwijderen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Signals Tab */}
        <TabsContent value="signals" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Signalen & Notificaties</CardTitle>
              <Button variant="outline" onClick={() => setSignalDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nieuw signaal
              </Button>
            </CardHeader>
            <CardContent>
              {signals.length === 0 ? (
                <div className="text-center py-12 text-[#57534E]">
                  <Bell className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Geen signalen</p>
                  <Button className="mt-4" variant="outline" onClick={() => setSignalDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Eerste signaal toevoegen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {signals.map(signal => {
                    const Icon = severityIcons[signal.severity];
                    const colors = severityColors[signal.severity];
                    return (
                      <div 
                        key={signal.id} 
                        className={`flex items-start gap-3 p-4 rounded-lg border transition-all ${
                          signal.is_read 
                            ? 'bg-stone-50 border-stone-100 opacity-60' 
                            : `${colors.bg} ${colors.border}`
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${colors.bg}`}>
                          <Icon className={`w-5 h-5 ${colors.text}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className={`${signal.is_read ? 'text-[#57534E]' : 'text-[#1C1917] font-medium'}`}>
                                {signal.message}
                              </p>
                              <p className="text-xs text-[#57534E] mt-1">
                                {formatDate(signal.created_at)}
                                {signal.type === 'handmatig' && (
                                  <span className="ml-2 text-amber-600">• Handmatig</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!signal.is_read && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleMarkSignalRead(signal.id)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Gelezen
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => handleDeleteSignal(signal.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Actielijst</CardTitle>
              <Button variant="outline" onClick={() => setActionDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe actie
              </Button>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <div className="text-center py-12 text-[#57534E]">
                  <CheckSquare className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Geen acties</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Actie</TableHead>
                      <TableHead>Toegewezen</TableHead>
                      <TableHead>Prioriteit</TableHead>
                      <TableHead>Deadline</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.sort((a, b) => {
                      const priorityOrder = { hoog: 0, normaal: 1, laag: 2 };
                      return priorityOrder[a.priority] - priorityOrder[b.priority];
                    }).map(task => (
                      <TableRow key={task.id} className="hover:bg-stone-50">
                        <TableCell>
                          <Checkbox
                            checked={task.status === 'voltooid'}
                            onCheckedChange={(checked) => 
                              handleUpdateTaskStatus(task.id, checked ? 'voltooid' : 'open')
                            }
                          />
                        </TableCell>
                        <TableCell className={task.status === 'voltooid' ? 'line-through text-[#57534E]' : 'font-medium'}>
                          <div>
                            {task.title}
                            {task.goal_id && (
                              <span className="ml-2 text-xs text-[#C2410C]">
                                <Target className="w-3 h-3 inline mr-1" />
                                {goals.find(g => g.id === task.goal_id)?.title?.substring(0, 15)}...
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{task.assigned_to ? getEmployeeName(task.assigned_to) : '-'}</TableCell>
                        <TableCell>
                          <Badge className={`${priorityConfig[task.priority]?.bg} ${priorityConfig[task.priority]?.text}`}>
                            {task.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>{task.due_date ? formatDate(task.due_date) : '-'}</TableCell>
                        <TableCell>
                          <Badge className={
                            task.status === 'voltooid' ? 'bg-emerald-100 text-emerald-700' :
                            task.status === 'bezig' ? 'bg-amber-100 text-amber-700' :
                            'bg-stone-100 text-stone-700'
                          }>
                            {task.status || 'open'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Notities</CardTitle>
              <Button variant="outline" onClick={() => setNoteDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe notitie
              </Button>
            </CardHeader>
            <CardContent>
              {notes.length === 0 ? (
                <div className="text-center py-12 text-[#57534E]">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Nog geen notities</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {notes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(note => {
                    const catConfig = categoryConfig[note.category] || categoryConfig.algemeen;
                    const CatIcon = catConfig.icon;
                    return (
                      <Card key={note.id} className="card-base hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center`}>
                                <CatIcon className={`w-4 h-4 ${catConfig.color}`} />
                              </div>
                              <div>
                                <p className="text-sm font-medium capitalize">{note.category || 'Algemeen'}</p>
                                <p className="text-xs text-[#57534E]">Week {note.week_number}, {note.year}</p>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-rose-600 h-6 w-6 p-0"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm whitespace-pre-wrap line-clamp-4">{note.content}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evaluations Tab */}
        <TabsContent value="evaluations" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Week & Maand Evaluaties</CardTitle>
              <Button variant="outline" onClick={() => setEvaluationDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nieuwe evaluatie
              </Button>
            </CardHeader>
            <CardContent>
              {evaluations.length === 0 ? (
                <div className="text-center py-12 text-[#57534E]">
                  <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Nog geen evaluaties</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {evaluations.sort((a, b) => new Date(b.date) - new Date(a.date)).map(evalItem => (
                    <Card 
                      key={evalItem.id} 
                      className="card-base cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => openEvaluationDetail(evalItem)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Badge className={
                              evalItem.type === 'week' ? 'bg-sky-100 text-sky-700' : 
                              evalItem.type === 'maand' ? 'bg-amber-100 text-amber-700' : 
                              'bg-purple-100 text-purple-700'
                            }>
                              {evalItem.type} evaluatie
                            </Badge>
                            <span className="text-sm text-[#57534E]">{formatDate(evalItem.date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map(star => (
                                <Star 
                                  key={star}
                                  className={`w-4 h-4 ${star <= (evalItem.rating || 3) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} 
                                />
                              ))}
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#57534E]" />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-[#57534E]">Bespreekpunten</p>
                            <p className="font-medium">{evalItem.topics?.length || 0} onderwerpen</p>
                          </div>
                          <div>
                            <p className="text-[#57534E]">Afspraken</p>
                            <p className="font-medium">{evalItem.agreements?.length || 0} actiepunten</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bedrijfsoverzicht Tab */}
        <TabsContent value="bedrijfsoverzicht" className="mt-6 space-y-6" data-testid="bedrijfsoverzicht-tab">
          <BedrijfsOverzicht 
            revenue={revenue}
            costs={costs}
            goals={goals}
            tasks={tasks}
            campaigns={campaigns}
            events={events}
            arrangements={arrangements}
            ideas={ideas}
            reviews={reviews}
            businessSettings={businessSettings}
            overviewShares={overviewShares}
            setOverviewShares={setOverviewShares}
            overviewShareDialog={overviewShareDialog}
            setOverviewShareDialog={setOverviewShareDialog}
            overviewShareForm={overviewShareForm}
            setOverviewShareForm={setOverviewShareForm}
            overviewShareLoading={overviewShareLoading}
            setOverviewShareLoading={setOverviewShareLoading}
            api={api}
          />
        </TabsContent>
      </Tabs>

      {/* Evaluation Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl">
          {selectedEvaluation && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-3">
                    <Badge className={
                      selectedEvaluation.type === 'week' ? 'bg-sky-100 text-sky-700' : 
                      selectedEvaluation.type === 'maand' ? 'bg-amber-100 text-amber-700' : 
                      'bg-purple-100 text-purple-700'
                    }>
                      {selectedEvaluation.type} evaluatie
                    </Badge>
                    {formatDate(selectedEvaluation.date)}
                  </DialogTitle>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star 
                        key={star}
                        className={`w-5 h-5 ${star <= (selectedEvaluation.rating || 3) ? 'text-amber-500 fill-amber-500' : 'text-stone-300'}`} 
                      />
                    ))}
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-6 pt-4">
                {/* Topics */}
                {selectedEvaluation.topics && selectedEvaluation.topics.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-[#C2410C]" />
                      Besproken onderwerpen
                    </h3>
                    <div className="space-y-2">
                      {selectedEvaluation.topics.map((topic, i) => (
                        <div key={i} className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>{topic.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Agreements */}
                {selectedEvaluation.agreements && selectedEvaluation.agreements.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <CheckSquare className="w-4 h-4 text-[#C2410C]" />
                      Afspraken / Actiepunten
                    </h3>
                    <div className="space-y-2">
                      {selectedEvaluation.agreements.map((agr, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                          <Checkbox checked={agr.completed} disabled />
                          <span className="flex-1">{agr.text}</span>
                          {agr.assignee && (
                            <Badge variant="outline">{getEmployeeName(agr.assignee)}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Notes */}
                {selectedEvaluation.notes && (
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#C2410C]" />
                      Notities
                    </h3>
                    <p className="p-3 bg-stone-50 rounded-lg text-sm">{selectedEvaluation.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t">
                  <Button 
                    variant="outline"
                    className="text-rose-600 border-rose-300"
                    onClick={() => handleDeleteEvaluation(selectedEvaluation.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Verwijderen
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActiesEvaluatiePage;
